import { eBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { commitmentOfferLocationQueryAtom } from "@/modules/metal/offer/offerApi.ts";
import type { MetalBillingPeriod } from "@/modules/metal/offer/period.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import type { Price } from "@/modules/pricing/resourcePricing.ts";
import { getBareMetalSpecPrice } from "@/modules/pricing/resourcePricing.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { MetalCommitmentOffer, MetalCommitmentPeriodEnum } from "@warrenio/api-spec/spec.oats.gen";

export interface MetalOffer<TPeriod extends MetalBillingPeriod = MetalBillingPeriod> {
    period_hours: TPeriod;
    price: number;
}

export interface OfferEligibility {
    /** Account is LIMITED and the selected offer is a paid reservation */
    isBlockedForLimited: boolean;
    /** Pre-pay account without enough remaining credit to cover the offer */
    hasNotEnoughCredit: boolean;
}

export interface MetalOffersState<TPeriod extends MetalBillingPeriod = MetalBillingPeriod> {
    specOffers: MetalOffer<TPeriod>[];
    account: EBillingAccount | null;
    location: string;
    /** Only set when `includeHourly` - feeds the non-reservation branch of the cost field */
    hourlyPrice: Price | undefined;
    getEligibility: (period: MetalBillingPeriod | null | undefined) => OfferEligibility;
}

interface MetalOffersOptions {
    location: string;
    specUuid: string | null;
    billingAccountId: number | null | undefined;
    /** Prepend the hourly (non-reservation) offer */
    includeHourly?: boolean;
}

/**
 * Offers available for a single spec.
 *
 * NB: The hourly (non-reservation) offer is not returned by the API, it is synthesized from the price list. Pass
 * `hourlyPrice` to prepend it.
 */
function getSpecOffers(
    offers: MetalCommitmentOffer[],
    specUuid: string | null,
): MetalOffer<MetalCommitmentPeriodEnum>[];
function getSpecOffers(
    offers: MetalCommitmentOffer[],
    specUuid: string | null,
    hourlyPrice: number | undefined,
): MetalOffer<MetalBillingPeriod>[];
function getSpecOffers(
    offers: MetalCommitmentOffer[],
    specUuid: string | null,
    hourlyPrice?: number,
): MetalOffer<MetalBillingPeriod>[] {
    const specOffers: MetalOffer<MetalBillingPeriod>[] = offers
        .filter((offer) => offer.machine_spec_uuid === specUuid)
        .map((offer) => ({ ...offer, price: Number(offer.price) }));

    return hourlyPrice != null ? [{ period_hours: 0, price: hourlyPrice }, ...specOffers] : specOffers;
}

export function findOffer<TPeriod extends MetalBillingPeriod>(
    offers: readonly MetalOffer<TPeriod>[],
    period: MetalBillingPeriod | null | undefined,
): MetalOffer<TPeriod> | undefined {
    return period == null ? undefined : offers.find((item) => item.period_hours === period);
}

function getOfferEligibility(
    account: EBillingAccount | null | undefined,
    offer: MetalOffer | undefined,
): OfferEligibility {
    return {
        isBlockedForLimited: !!(account?.isLimited && offer && offer.period_hours > 0),
        hasNotEnoughCredit: !!(account?.isPrePay && offer && offer.price > (account.remainingCredit ?? 0)),
    };
}

export function useMetalOffers(
    options: MetalOffersOptions & { includeHourly: true },
): MetalOffersState<MetalBillingPeriod>;
export function useMetalOffers(
    options: MetalOffersOptions & { includeHourly?: false },
): MetalOffersState<MetalCommitmentPeriodEnum>;

export function useMetalOffers({
    location,
    specUuid,
    billingAccountId,
    includeHourly,
}: MetalOffersOptions): MetalOffersState {
    // NB: Always fetch the offers for the whole location (and filter by spec below) so both the create form and the
    // reservation modal share a single query. The per-spec endpoint may omit `machine_spec_uuid` from its response,
    // which would make the filter drop everything.
    const offers = useSuspenseQueryAtom(commitmentOfferLocationQueryAtom(location));
    const priceList = useSuspenseQueryAtom(pricesAtom);
    const billingAccounts = useSuspenseQueryAtom(eBillingAccountQueryAtom);

    const account = (billingAccountId != null ? billingAccounts.get(billingAccountId) : null) ?? null;
    const hourlyPrice =
        includeHourly && specUuid != null ? getBareMetalSpecPrice(priceList, location, specUuid) : undefined;
    const specOffers = getSpecOffers(offers, specUuid, hourlyPrice?.hourly);

    return {
        specOffers,
        account,
        location,
        hourlyPrice,
        // NB: A function (instead of a plain value) since the selected period is only known to the caller, which needs
        // the eligibility for its submit button while the form itself is built from `specOffers`.
        getEligibility: (period) => getOfferEligibility(account, findOffer(specOffers, period)),
    };
}
